<template>
  <div>
    <div class="comment-card mb-4">
      <div class="d-flex justify-content-between align-items-start">
        <div class="mr-4">
           <router-link :to="{ name: 'users.show', params: { username: comment.user.username } }">
            <div class="author-image-circle background-cover shadow-light" :style="{ backgroundImage: `url('${comment.user.avatar}')` }"></div>
          </router-link>
        </div>
        <div class="flex-grow-1">
          <div class="commentContent">
            <p class="font-size-small">{{ comment.content }}</p>
          </div>
          <!-- FOOTER -->
          <div
            class="mt-4 bordered-top-1 pt-2 d-flex flex-wrap flex-sm-row justify-content-between align-items-center font-size-xSmall"
            style="border-color: rgba(29,29,29,0.3);"
          >
            <div class="d-flex flex-row flex-sm-fill justify-content-between justify-content-md-start response-actions-width align-items-center">
              <span class="mr-4">
                <router-link :to="{ name: 'users.show', params: { username: comment.user.username } }" class="text-color1 d-flex justify-content-start align-items-center">
                  <BaseIcon
                    size="xSmall"
                  >
                    <UserIcon />
                  </BaseIcon>
                  {{ comment.user.name }}
                  <BaseIcon
                    v-if="comment.user && comment.user.is_verified"
                    class="ml-1"
                    size="xxSmall"
                    color="color2"
                  >
                      <VerifiedIcon
                        class="profile"
                      />
                    </BaseIcon>
                </router-link>
              </span>
              <span>
                <BaseIcon
                  size="xSmall"
                >
                  <CalendarIcon />
                </BaseIcon>
                {{ comment.created_at | formatDate('DD.MM.YYYY') }}
              </span>
            </div>
            <div
              class="d-flex flex-row flex-sm-fill justify-content-between justify-content-md-end mt-2 mt-md-0 response-actions-width"
              v-if="user"
            >
              <span>
                <Button
                  class="btn-clean text-color2 font-size-xSmall hover-color-color1"
                  @click="toggleFavorite"
                >
                  <template
                    v-if="!isFavoriting"
                  >
                    {{ comment.favorites_count }}
                    <template
                      v-if="!comment.is_favorited"
                    >
                      <BaseIcon
                        size="xSmall"
                      >
                        <HeartIcon />
                      </BaseIcon>
                      Curtir
                    </template>
                    <template
                      v-else-if="comment.is_favorited"
                    >
                      <BaseIcon
                        size="xSmall"
                      >
                        <HeartFilledIcon />
                      </BaseIcon>
                      Curtido
                    </template>
                  </template>
                  <Loader
                    color="color2"
                    v-else
                  />
                </Button>
              </span>
              <span class="ml-lg-2">
                <Button
                  class="btn-clean text-color2 font-size-xSmall hover-color-color1"
                  @click="isReplying = true"
                >
                  <BaseIcon
                    size="xSmall"
                  >
                    <ReplyIcon />
                  </BaseIcon>
                  Responder
                </Button>
              </span>
              <span class="ml-lg-2" v-if="isOwnedByUser || isAuthor">
                <Button
                  class="btn-clean text-color2 font-size-xSmall hover-color-color1"
                  @click="deleteComment"
                >
                  <template v-if="!isDeleting">
                    <BaseIcon
                      size="xSmall"
                    >
                      <TrashIcon />
                    </BaseIcon>
                    Excluir
                  </template>
                  <Loader
                    color="color2"
                    v-else
                  />
                </Button>
              </span>
            </div>
          </div> <!-- END FOOOTER -->
          <!-- REPLY FORM -->
          <div>
            <CommentForm
              :entityId="comment.entity.id"
              :entityType="comment.type"
              :user="user"
              :parentId="comment.parent ? comment.parent : comment.id"
              :isVisible="isReplying"
              @comment-created="replyCreated"
              @cancel-comment="isReplying = false"
              v-if="isReplying"
            />
          </div><!-- END REPLY FORM -->
          <!-- REPLIES -->
          <div
            class="mt-2"
            v-if="comment.replies_count > 0"
          >
            <Button
              class="btn-clean font-size-small font-weight-bold text-color2 hover-text-color1"
              @click="toggleReplies"
            >
              <i
                class="fa mr-1"
                :class="{
                  'fa-caret-right' : !isRepliesVisible,
                  'fa-caret-down' : isRepliesVisible,
                }"
              ></i>
              {{ isRepliesVisible ? 'Ocultar' : 'Ver' }} {{ comment.replies_count }} {{ comment.replies_count > 1 ? 'respostas' : 'resposta' }}
            </Button>

            <div
              class="mt-4 comment-list text-left"
              v-if="isRepliesVisible && replies.length > 0"
            >
              <CommentCard
                v-for="reply in replies" :key="reply.id"
                :comment="reply"
                :isAuthor="isAuthor"
                @reply-created="replyCreated"
                @comment-deleted="replyDeleted"
                @favorite-toggled="replyFavoriteToggled"
              />

              <Button
                class="btn btn-primary w-100 font-size-xSmall"
                @click="loadMore"
                :loading="loader"
                v-if="isLoadMoreVisible"
              >
                Carregar mais respostas
              </Button>
            </div>
            <div
              class="text-center"
              v-else-if="loader"
            >
              <Loader
                color="color2"
                size="big"
              />
            </div>
          </div><!-- END REPLIES -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import CommentsService from '@/modules/comments/services/comments-service'
import FavoritesService from '@/modules/favorites/services/favorites-service'
import { mapState } from 'vuex'

import Button from '@/components/common/Button/Button.vue'
import BaseIcon from '@/components/common/Icon/BaseIcon.vue'
import CalendarIcon from '@/components/common/Icon/CalendarIcon.vue'
import CommentForm from '@/components/comment/CommentForm.vue'
import HeartIcon from '@/components/common/Icon/HeartIcon'
import HeartFilledIcon from '@/components/common/Icon/HeartFilledIcon'
import Loader from '@/components/common/Loader/Loader.vue'
import ReplyIcon from '@/components/common/Icon/ReplyIcon.vue'
import TrashIcon from '@/components/common/Icon/TrashIcon.vue'
import UserIcon from '@/components/common/Icon/UserIcon.vue'
import VerifiedIcon from '@/components/common/Icon/VerifiedIcon'

export default {
  name: 'CommentCard',
  props: ['comment', 'isAuthor'],
  components: {
    Button,
    BaseIcon,
    CalendarIcon,
    CommentForm,
    HeartIcon,
    HeartFilledIcon,
    Loader,
    ReplyIcon,
    TrashIcon,
    UserIcon,
    VerifiedIcon
  },
  data () {
    return {
      isDeleting: false,
      isFavoriting: false,
      isReplying: false,
      isRepliesVisible: false,
      page: 1,
      replies: [],
      isLoadMoreVisible: true,
      loader: false
    }
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    }),
    isOwnedByUser () {
      return (this.user && (this.comment.user.id === this.user.id))
    }
  },
  methods: {
    async deleteComment () {
      this.isDeleting = true
      await CommentsService.deleteComment(this.comment.id)
      this.isDeleting = false
      this.$emit('comment-deleted', this.comment)
    },
    toggleReplies () {
      this.isRepliesVisible = !this.isRepliesVisible
      if (this.replies.length <= 0) {
        this.loadReplies()
      }
    },
    async loadReplies () {
      this.loader = true
      try {
        const replies = await CommentsService.getComments({ parentId: this.comment.id, page: this.page, modelType: this.comment.type })
        this.replies = [...this.replies, ...replies]
        this.isLoadMoreVisible = replies.length > 0
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    },
    loadMore () {
      this.page++
      this.loadReplies()
    },
    replyCreated (reply) {
      if (this.isRepliesVisible) this.replies = [...this.replies, reply]
      else this.toggleReplies()
      this.isReplying = false
      this.$emit('reply-created', reply)
    },
    replyDeleted (reply) {
      const index = this.replies.findIndex(r => r.id === reply.id)
      console.log(index)
      this.replies.splice(index, 1)
      this.$emit('reply-deleted', reply)
    },
    async refreshReply (id) {
      const index = this.replies.findIndex(r => r.id === id)
      const reply = await CommentsService.getComment(id)
      this.replies.splice(index, 1, reply)
    },
    async toggleFavorite () {
      this.isFavoriting = true
      try {
        const action = this.comment.is_favorited ? 'destroy' : 'create'
        await FavoritesService.toggleFavorite(this.comment.id, 'comment', action)
        this.$emit('favorite-toggled', this.comment)
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.isFavoriting = false
    },
    replyFavoriteToggled (reply) {
      this.refreshReply(reply.id)
    }
  }
}
</script>

<style lang="scss" scoped>
.commentContent {
  white-space: pre-wrap;
  line-height: 1.2;
}

.response-actions-width {
  width: 100%;

  @include tablet {
    width: auto;
  }
}
</style>
